import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { toast } from "sonner";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import ArtizenIdCard from "@/components/ArtizenIdCard";

const TIERS = [
  {
    key: "artizen",
    name: "The Artizen",
    price: "Free",
    free: true,
    accent: "hl-cream",
    color: "cream",
    perks: [
      "Artizen ID — permanent, sequential (AZ-00001 format)",
      "Listing in The Discovery Project + public profile",
      "IYKYK newsletter",
      "No Pressure — full archive",
      "The Edit — full access",
      "Community events — observer",
    ],
  },
  {
    key: "circle",
    name: "The Circle",
    price: "₹199",
    accent: "hl-coral",
    color: "primary",
    perks: [
      "Everything in The Artizen",
      "Creative Commons — subsidised resource access",
      "Partner discounts — studios, gear, spaces",
      "Unfair Advantage advisory — discounted rate",
      "Creative Class nomination rights",
      "Community events — full participant",
      "Peer network + curated introductions",
    ],
  },
  {
    key: "house",
    name: "The House",
    price: "₹599",
    accent: "hl-cyan",
    color: "secondary",
    perks: [
      "Everything in The Circle",
      "Multi-seat — up to 5 seats (bands, troupes, collectives)",
      "Unfair Advantage advisory — 2 sessions included / month",
      "Priority access to The Lab partnerships",
      "Fellowship eligible",
      "Early access — new verticals + pilots",
      "Dedicated community manager",
    ],
  },
];

export default function ArtizenPass() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [activePass, setActivePass] = useState(null);

  useEffect(() => {
    if (!user) { setActivePass(null); return; }
    api.get("/pass/me").then(({ data }) => setActivePass(data?.has_pass ? data : null)).catch(() => setActivePass(null));
  }, [user]);

  const subscribe = async (tier) => {
    if (!user) { loginWithGoogle("/artizen-pass"); return; }
    setLoading(tier);
    try {
      const { data } = await api.post("/subscriptions/recurring", { tier });
      if (data.mock_mode || !data.razorpay_subscription_id) {
        // No Razorpay keys configured — mock activation flow
        await api.post("/subscriptions/recurring/verify", { subscription_id: data.subscription_id });
        toast.success(`${tier.toUpperCase()} tier activated · (mock mode — add Razorpay keys for real billing)`);
        setLoading(null);
        return;
      }
      const isOneTime = !!data.razorpay_order_id;
      const options = isOneTime ? {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "anArtizen",
        description: `Artizen Pass · ${tier.toUpperCase()} · One month`,
        theme: { color: "#F25C50" },
        prefill: { name: user.name, email: user.email },
        handler: async (resp) => {
          try {
            await api.post("/subscriptions/recurring/verify", {
              subscription_id: data.subscription_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Welcome to ${tier.toUpperCase()} \u{1F389}`);
            const r = await api.get("/pass/me");
            if (r.data?.has_pass) setActivePass(r.data);
          } catch (e) { toast.error("Verification failed"); }
        },
        modal: { ondismiss: () => setLoading(null) },
      } : {
        key: data.key_id,
        subscription_id: data.razorpay_subscription_id,
        name: "anArtizen",
        description: `Artizen Pass · ${tier.toUpperCase()} · Monthly`,
        theme: { color: "#F25C50" },
        prefill: { name: user.name, email: user.email },
        handler: async (resp) => {
          try {
            await api.post("/subscriptions/recurring/verify", {
              subscription_id: data.subscription_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Welcome to ${tier.toUpperCase()} \u{1F389} \u00b7 You will be billed monthly.`);
            const r = await api.get("/pass/me");
            if (r.data?.has_pass) setActivePass(r.data);
          } catch (e) { toast.error("Verification failed"); }
        },
        modal: { ondismiss: () => setLoading(null) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    } finally { setLoading(null); }
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="MEMBERSHIP" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-16">
          <SectionLabel>Membership</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            Free to join. <br /> <span className="hl-coral">Deeper if you stay.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
            Every member gets a permanent <span className="hl-cyan font-medium">Artizen ID</span> the moment they join. The Circle and The House add subsidised resources, advisory, and access to Artizen Lab in Delhi.
          </p>
          <p className="meta-mono mt-6 !text-white/55 max-w-2xl">
            Artist management is by application only and is independent of the membership programme. Membership is not a factor in management decisions.
          </p>
        </div>
      </section>

      {activePass && (
        <section className="border-b border-border bg-[#0c0c0e]">
          <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
              <div>
                <SectionLabel><Sparkles size={11} /> You&apos;re in</SectionLabel>
                <h2 className="font-display text-3xl md:text-5xl font-bold mt-2">Your <span className="hl-coral">Artizen ID</span> is ready.</h2>
                <p className="meta-mono mt-3">— MEMBER {activePass.pass_code}</p>
              </div>
            </div>
            <ArtizenIdCard embed />
          </div>
        </section>
      )}

      <section className="border-b border-border">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
          <div className="grid md:grid-cols-3 gap-px bg-border">
            {TIERS.map((t, i) => (
              <div key={t.key} id={t.key} className="bg-background p-8 md:p-10 flex flex-col min-h-[520px] group hover:bg-white/[0.02] transition scroll-mt-24">
                <p className="meta-mono mb-6">— {t.name.toUpperCase()}</p>
                <p className={`num-display text-6xl md:text-7xl font-bold leading-none text-${t.color}`}>
                  {t.price}{!t.free && <span className="text-2xl text-white/40 font-mono ml-1">/mo</span>}
                </p>
                {t.free && <p className="meta-mono mt-2 !text-white/55">— FOREVER · NO CARD REQUIRED</p>}
                <ul className="mt-10 space-y-4 flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-white/85">
                      <Check size={14} className={`mt-1 text-${t.color} shrink-0`} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                {t.free ? (
                  <button
                    data-testid={`subscribe-${t.key}`}
                    onClick={() => { if (!user) loginWithGoogle("/dashboard"); else window.location.href = "/dashboard"; }}
                    className="btn-pill mt-10"
                  >
                    {user ? "Open dashboard" : "Join free"} <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    data-testid={`subscribe-${t.key}`}
                    onClick={() => subscribe(t.key)}
                    disabled={loading === t.key}
                    className={`btn-pill mt-10 ${i === 1 ? "btn-coral" : ""}`}
                  >
                    {loading === t.key ? "Starting…" : <>Join {t.name} <ArrowRight size={14} /></>}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Artizen Lab sub-tier removed — The Lab is now the brand vertical, see /lab */}
        </div>
      </section>

      <MemberOnlyFeatures user={user} activePass={activePass} />

      <section className="border-b border-border bg-[#0c0c0e]">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
          <p className="font-serif-italic text-xl md:text-3xl text-white/85 text-center">
            One bill. Studios, gear, advisors, residencies, courses — all at member rates. Cancel anytime.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid md:grid-cols-3 gap-px bg-border">
          <Why title="Cheaper than your phone bill" body="And gives you more." />
          <Why title="Member rates everywhere" body="On studios, gear, courses, advisory." />
          <Why title="The room you've been looking for" body="Peers, advisors, audience — under one roof." />
        </div>
      </section>
    </PageShell>
  );
}
function Why({ title, body }) {
  return (
    <div className="bg-background p-8">
      <p className="meta-mono !text-primary">→ WHY MEMBERS LOVE IT</p>
      <p className="font-display text-3xl font-bold mt-3">{title}</p>
      <p className="meta-mono mt-3 !text-white/70">{body}</p>
    </div>
  );
}

function MemberOnlyFeatures({ user, activePass }) {
  const tier = activePass?.tier || (user ? "artizen" : null);
  if (!user || !tier) return null;
  const unlocked = tier === "circle" || tier === "house";
  return (
    <section className="border-b border-border" data-testid="member-only-features">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
        <p className="meta-mono mb-6">— YOUR MEMBER FEATURES · {(activePass?.tier_label || "THE ARTIZEN · FREE").toUpperCase()}</p>
        <div className="grid md:grid-cols-2 gap-px bg-border">
          <FeatureCard
            unlocked={unlocked}
            label="Creative Commons"
            blurb={unlocked
              ? "Offer your skills, studio or gear at subsidised rates — and tap the network when you need to."
              : "Members in The Circle and The House offer subsidised studios, gear and time to each other."}
            cta={unlocked ? "Open Creative Commons" : "Upgrade to The Circle"}
            to={unlocked ? "/commons" : "/membership#circle"}
            testid="feat-commons"
          />
          <FeatureCard
            unlocked={unlocked}
            label="Creative Class"
            blurb={unlocked
              ? "An annual honour list. As a member, you can nominate practitioners you think the country should know about."
              : "Members nominate the practitioners that go on the annual Creative Class list. Upgrade to nominate."}
            cta={unlocked ? "Open Creative Class" : "Upgrade to The Circle"}
            to={unlocked ? "/creative-class" : "/membership#circle"}
            testid="feat-creative-class"
          />
        </div>
        {!unlocked && (
          <p className="meta-mono mt-6 !text-white/50">— FREE MEMBERS GET A PERMANENT ARTIZEN ID, LISTING IN THE DISCOVERY PROJECT, AND THE EDIT ARCHIVE.</p>
        )}
      </div>
    </section>
  );
}

function FeatureCard({ unlocked, label, blurb, cta, to, testid }) {
  return (
    <div className={`bg-background p-8 ${unlocked ? "" : "opacity-70"}`} data-testid={testid}>
      <div className="flex items-center gap-3">
        <p className="meta-mono" style={{ color: unlocked ? "#38bdf8" : "rgba(255,255,255,0.45)" }}>
          — {label.toUpperCase()} {unlocked ? "· UNLOCKED" : "· LOCKED"}
        </p>
      </div>
      <p className="font-display text-3xl md:text-4xl font-bold mt-4 leading-tight">{label}</p>
      <p className="mt-4 text-white/80 text-sm leading-relaxed max-w-md">{blurb}</p>
      <Link to={to} className={`btn-pill mt-6 inline-flex ${unlocked ? "btn-coral" : ""}`}>
        {cta} <ArrowRight size={14} />
      </Link>
    </div>
  );
}
